import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Progress, TextInput } from '@mantine/core';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import MediaDropzone from '../media/MediaDropzone';
import AppText from '../../common/AppText';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import StoreProductsSelect from '../wizard/StoreProductsSelect';
import { useQuickCanvasController } from './helpers/fabricCanvasHelper';
import { ECOM_PRODUCT_STATUS_ENUM } from '../../../config/constants';

const initialFormState = {
  designName: '',
  imageUrl: null,
  pkEcomStoreMedia: null,
  view: 'upload_image',
  pkEcomExternalProducts: []
};

const PrintDesignProductQuickCreateModal = ({
  isOpen,
  onClose,
  fkEcomStore,
  fkEcomVendor,
  onComplete
}) => {
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchStoreCreateOptions,
    createStoreProductsFromPrintTemplates
  } = useContext(StoreContext);
  const {
    hasBeenCancelled,
    uploadState,
    onQuickCreateDesignProducts,
    onCancelCreateDesignProducts
  } = useQuickCanvasController({
    uploadUrlBase: fkEcomStore
      ? `ecom-store-${fkEcomStore}`
      : fkEcomVendor
      ? `ecom-vendor-${fkEcomVendor}`
      : '',
    onCreateDesignProduct: (
      { storeDesignImageUrl, storeDesignName, printDesigns },
      onSuccessCallback,
      onErrorCallback
    ) => {
      createStoreProductsFromPrintTemplates(
        authState.pkEcomStore,
        {
          storeDesignImageUrl,
          storeDesignName,
          fkEcomProductStatus: ECOM_PRODUCT_STATUS_ENUM.ACTIVE,
          printDesigns: printDesigns.map((d) => ({
            ...d,
            fkEcomVendorDesign: d.pkDesign
          }))
        },
        onSuccessCallback,
        onErrorCallback
      );
    }
  });
  const [formState, setFormState] = useState({ ...initialFormState });

  const {
    loading: createOptionsLoading,
    value: createOptions
  } = state.ecomStoreCreateOptions;

  useEffect(() => {
    if (isOpen) {
      setFormState({ ...initialFormState });

      if (!createOptions && !createOptionsLoading) {
        fetchStoreCreateOptions(null, triggerNotification);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (uploadState.completed && onComplete) {
      onComplete(uploadState.responseData);
    }
  }, [uploadState]);

  return (
    <ResponsiveModal
      formSectionProps={
        formState.view === 'create_products'
          ? {
              onCancel: hasBeenCancelled
                ? onClose
                : () => {
                    onCancelCreateDesignProducts();
                  },
              isSubmitHidden: true,
              cancelTitle: hasBeenCancelled ? 'Close' : 'Cancel',
              cancelColor: hasBeenCancelled ? 'dark' : '#C40000'
            }
          : formState.view === 'product_select'
          ? {
              onCancel: () => {
                setFormState({
                  ...formState,
                  view: 'upload_image'
                });
              },
              onSubmit: () => {
                setFormState({
                  ...formState,
                  view: 'create_products'
                });

                const selectedProducts = createOptions.ecomExternalProducts.filter(
                  (p) =>
                    formState.pkEcomExternalProducts.includes(
                      p.pkEcomExternalProduct
                    )
                );
                onQuickCreateDesignProducts({
                  pkEcomStoreMedia: formState.pkEcomStoreMedia,
                  imageUrl: formState.imageUrl,
                  ecomExternalProducts: selectedProducts,
                  storeDesignName: formState.designName,
                  isPrivate: true
                });
              },
              submitColor: 'dark',
              submitTitle: 'Create products',
              isSubmitDisabled: formState.pkEcomExternalProducts.length === 0,
              cancelTitle: 'Back',
              isLoading: formState.loading
            }
          : {
              onCancel: onClose,
              onSubmit: () => {
                setFormState({
                  ...formState,
                  view: 'product_select'
                });
              },
              submitColor: 'dark',
              submitTitle: 'Continue',
              isSubmitDisabled: !formState.imageUrl,
              cancelTitle: 'Cancel',
              isLoading: formState.loading
            }
      }
      onClose={uploadState.loading ? () => {} : onClose}
      opened={isOpen}
      size={600}
      title="Design products"
    >
      {formState.view === 'create_products' ? (
        <AppStack style={{ gap: 20 }}>
          <AppStack style={{ flex: 1, gap: 5 }}>
            <Progress
              animated
              value={
                (Math.min(
                  uploadState.completedPks.length + 1,
                  uploadState.uploadPks.length
                ) /
                  uploadState.uploadPks.length) *
                100
              }
            />
            {hasBeenCancelled ? (
              <AppText
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  textAlign: 'center',
                  color: '#C40000'
                }}
              >
                Products cancelled. Attempted (
                {Math.min(
                  uploadState.completedPks.length + 1,
                  uploadState.uploadPks.length
                )}
                /{uploadState.uploadPks.length})
              </AppText>
            ) : (
              <AppText
                style={{ fontSize: 14, fontWeight: 500, textAlign: 'center' }}
              >
                Creating product designs... (
                {Math.min(
                  uploadState.completedPks.length + 1,
                  uploadState.uploadPks.length
                )}
                /{uploadState.uploadPks.length})
              </AppText>
            )}
          </AppStack>
        </AppStack>
      ) : formState.view === 'upload_image' ? (
        <AppStack>
          <AppText style={{ fontSize: 14, color: '#666', textAlign: 'center' }}>
            Upload an image and set a design name to auto create products.
          </AppText>

          <TextInput
            description="This will be added to the end of each product name created."
            label="Design Name"
            onChange={(e) =>
              setFormState({
                ...formState,
                designName: e.currentTarget.value
              })
            }
            required
            value={formState.designName}
          />

          <MediaDropzone
            disabled={formState.loading}
            enableMediaSelect
            files={[]}
            fkEcomStore={fkEcomStore}
            fkEcomVendor={fkEcomVendor}
            height={200}
            maxSelectCount={1}
            onUpload={(media) => {
              setFormState({
                ...formState,
                imageUrl: media[0]?.src,
                pkEcomStoreMedia: media[0]?.pkEcomStoreMedia
              });
            }}
            selectedPreview={formState.imageUrl}
            uploadOnDrop
          />

          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Products designs are grouped by matching image url or design name.
          </AppText>
        </AppStack>
      ) : (
        <AppStack>
          <StoreProductsSelect
            loading={createOptionsLoading}
            onSelectProducts={(selectedProducts) => {
              setFormState({
                ...formState,
                pkEcomExternalProducts: selectedProducts
              });
            }}
            products={
              createOptions?.ecomExternalProducts.map((p) => ({
                value: p.pkEcomExternalProduct,
                name: p.name,
                previewImage: p.previewImage
              })) || []
            }
            selectedProductIds={formState.pkEcomExternalProducts}
          />
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

PrintDesignProductQuickCreateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fkEcomStore: PropTypes.string,
  fkEcomVendor: PropTypes.string,
  onComplete: PropTypes.func
};

export default PrintDesignProductQuickCreateModal;
